import React from "react";
import { Lock } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "@/hooks/useAuth";
import {
  updateUserEmail,
  updateUserPassword,
  deleteUserAccount,
} from "@/store/slices/authSlice";
import SettingsSection from "./SettingsSection";

const Account = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.auth);

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const current_password = e.target.password.value;

    try {
      const result = await dispatch(
        updateUserEmail({ email, current_password })
      ).unwrap();
      toast.success(result.message || "Email updated successfully!");
      e.target.reset();
    } catch (err) {
      toast.error(err.message || "Failed to update email.");
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    const current_password = e.target.current_password.value;
    const new_password = e.target.new_password.value;
    const confirm_password = e.target.confirm_password.value;

    try {
      const result = await dispatch(
        updateUserPassword({ current_password, new_password, confirm_password })
      ).unwrap();
      toast.success(result.message || "Password updated successfully!");
      e.target.reset();
    } catch (err) {
      toast.error(err.message || "Failed to update password.");
    }
  };

  const handleAccountDelete = async (e) => {
    e.preventDefault();
    const password = e.target.password.value;

    if (
      !window.confirm("Are you sure you want to delete your account?")
    ) {
      return;
    }

    try {
      const result = await dispatch(deleteUserAccount({ password })).unwrap();
      toast.success(result.message || "Account deleted successfully.");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(err.message || "Failed to delete account.");
    }
  };

  return (
    <SettingsSection title="Privacy & Account" icon={<Lock />}>
      <div className="space-y-5">
        {/* Change Email */}
        <form onSubmit={handleEmailUpdate} className="space-y-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200">
            Change Email
          </h3>
          <input
            type="email"
            name="email"
            placeholder="New email"
            required
            className="input-field"
          />
          <input
            type="password"
            name="password"
            placeholder="Current password"
            required
            className="input-field"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-yellow-500 text-white rounded-md py-2 hover:bg-yellow-600 btn-anim disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Updating..." : "Update Email"}
          </button>
        </form>

        {/* Change Password */}
        <form onSubmit={handlePasswordUpdate} className="space-y-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200">
            Change Password
          </h3>
          <input
            type="password"
            name="current_password"
            placeholder="Current password"
            required
            className="input-field"
          />
          <input
            type="password"
            name="new_password"
            placeholder="New password"
            required
            className="input-field"
          />
          <input
            type="password"
            name="confirm_password"
            placeholder="Confirm new password"
            required
            className="input-field"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-green-600 text-white rounded-md py-2 hover:bg-green-700 btn-anim disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Updating..." : "Update Password"}
          </button>
        </form>

        {/* Danger Zone: Delete Account */}
        <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-6">
            <h3 className="text-lg font-bold text-red-700 dark:text-red-400 mb-2">
              Danger Zone
            </h3>
            <p className="text-sm text-red-600 dark:text-red-300 mb-6 opacity-90">
              Once you delete your account, there is no going back. Please be
              certain.
            </p>

            <form onSubmit={handleAccountDelete} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-red-200 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter password to confirm"
                  required
                  className="w-full bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/30 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 outline-none transition-shadow"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-red-600 text-white rounded-lg py-3 font-medium hover:bg-red-700 transition-colors shadow-lg shadow-red-500/20 btn-anim disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Deleting..." : "Delete My Account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
};

export default Account;

